import React, { useEffect, useState } from 'react';

import { ArrowLeft, Check, X } from 'lucide-react';

import {
	useCreateRevenueConfigurationMutation,
	useGetRevenueConfigurationByIdQuery,
	useUpdateRevenueConfigurationMutation,
} from '../../api';
import { useListPartnersQuery } from '../../api/partners';
import { useToast } from '../../context/ToastContext';
import { useFeature } from '../../hooks/useFeature';

import { v9, wizardStepClass, wizardStepNumClass } from './v9';
import BasicInfoStep from './wizard/BasicInfoStep';
import PartnerCoBrandingStep from './wizard/PartnerCoBrandingStep';
import RiskTierStep from './wizard/RiskTierStep';

import type { RevenueConfiguration } from './wizard/RiskTierStep';
import type { RevenueConfigTierInput, RiskTier } from '../../api';

interface RevenueConfigWizardProps {
	isOpen: boolean;
	onClose: () => void;
	onComplete: (configName: string) => void;
	editConfigId?: string;
	isFullPage?: boolean;
	onBack?: () => void;
}

type StepKey = 'basic' | 'partner' | 'rates';

const ALL_STEPS: { key: StepKey; name: string }[] = [
	{ key: 'basic', name: 'Basic Info' },
	{ key: 'partner', name: 'Partner Co-Branding' },
	{ key: 'rates', name: 'Risk Tier Rates' },
];

// Helper to create empty tier config
const createEmptyTierConfig = (tier: RiskTier): RevenueConfigTierInput => ({
	risk_tier: tier,
	// Legacy single rates
	// Storage format: percentage decimal (e.g., 0.0125 = 1.25 bps when converted)
	// User enters 1.25 bps → stored as 0.0125 → calculator divides by 100 → 0.000125
	discount_buy_rate: 0.0125, // Default 1.25 bps (stored as 0.0125, calculator uses 0.000125)
	authorization_buy_rate: 0.03, // Default $0.03
	// NEW: Split rates (optional, will fallback to legacy)
	discount_buy_rate_vmd: undefined,
	discount_buy_rate_amex: undefined,
	auth_buy_rate_standard: undefined,
	auth_buy_rate_pin_debit: undefined,
	auth_buy_rate_ebt: undefined,
	// NEW: Batch fee
	batch_fee_rate: 0,
	// NEW: Dynamic monthly fees
	monthly_fee_costs: [],
	// Legacy monthly fees
	monthly_fee_cost_1: 0,
	monthly_fee_cost_1_label: '',
	monthly_fee_cost_2: 0,
	monthly_fee_cost_2_label: '',
	monthly_fee_cost_3: 0,
	monthly_fee_cost_3_label: '',
	agent_revenue_share_percent: 50, // Default 50%
});

const RevenueConfigWizard: React.FC<RevenueConfigWizardProps> = ({
	isOpen,
	onClose,
	onComplete,
	editConfigId,
	isFullPage = false,
	onBack,
}) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [configuration, setConfiguration] = useState<RevenueConfiguration>({
		name: '',
		description: '',
		is_active: false,
		tiers: {
			LOW: createEmptyTierConfig('LOW'),
			MEDIUM: createEmptyTierConfig('MEDIUM'),
			HIGH: createEmptyTierConfig('HIGH'),
			OTHER: createEmptyTierConfig('OTHER'),
		},
	});

	const [createRevenueConfig, { isLoading: isCreating }] =
		useCreateRevenueConfigurationMutation();
	const [updateRevenueConfig, { isLoading: isUpdating }] =
		useUpdateRevenueConfigurationMutation();

	const { data: existingConfigResponse, isLoading: isLoadingConfig } =
		useGetRevenueConfigurationByIdQuery(editConfigId || '', {
			skip: !editConfigId,
		});

	const { showToast } = useToast();
	const partnerPrograms = useFeature('partner_programs');
	const STEPS = partnerPrograms
		? ALL_STEPS
		: ALL_STEPS.filter((step) => step.key !== 'partner');
	const stepKey = STEPS[currentStep - 1]?.key;
	const { data: partners = [] } = useListPartnersQuery(undefined, {
		skip: !partnerPrograms,
	});
	const linkedPartnerId =
		partners.find((p) => p.revenue_config_name === configuration.name)?.id ??
		'';
	// undefined until the user picks a partner: only a change is sent.
	const [pickedPartnerId, setPickedPartnerId] = useState<string>();

	// Load existing configuration when editing
	useEffect(() => {
		if (editConfigId && existingConfigResponse?.data) {
			const configs = existingConfigResponse.data;
			if (configs.length === 3 || configs.length === 4) {
				// Group by risk tier - support 3 or 4 tiers
				const tiersMap: Partial<Record<RiskTier, RevenueConfigTierInput>> = {
					LOW: createEmptyTierConfig('LOW'),
					MEDIUM: createEmptyTierConfig('MEDIUM'),
					HIGH: createEmptyTierConfig('HIGH'),
				};

				// Only add OTHER if it exists in the response
				const hasOtherTier = configs.some((c) => c.risk_tier === 'OTHER');
				if (hasOtherTier) {
					tiersMap.OTHER = createEmptyTierConfig('OTHER');
				}

				configs.forEach((config) => {
					tiersMap[config.risk_tier as RiskTier] = {
						risk_tier: config.risk_tier as RiskTier,
						// Legacy rates
						discount_buy_rate: config.discount_buy_rate,
						authorization_buy_rate: config.authorization_buy_rate,
						// NEW: Split discount rates
						discount_buy_rate_vmd: config.discount_buy_rate_vmd,
						discount_buy_rate_amex: config.discount_buy_rate_amex,
						// NEW: Split auth fees
						auth_buy_rate_standard: config.auth_buy_rate_standard,
						auth_buy_rate_pin_debit: config.auth_buy_rate_pin_debit,
						auth_buy_rate_ebt: config.auth_buy_rate_ebt,
						// NEW: Batch fee
						batch_fee_rate: config.batch_fee_rate,
						// NEW: Dynamic monthly fees (with fallback to legacy)
						monthly_fee_costs:
							config.monthly_fee_costs && config.monthly_fee_costs.length > 0
								? config.monthly_fee_costs
								: [],
						// Legacy monthly fees
						monthly_fee_cost_1: config.monthly_fee_cost_1,
						monthly_fee_cost_1_label: config.monthly_fee_cost_1_label,
						monthly_fee_cost_2: config.monthly_fee_cost_2,
						monthly_fee_cost_2_label: config.monthly_fee_cost_2_label,
						monthly_fee_cost_3: config.monthly_fee_cost_3,
						monthly_fee_cost_3_label: config.monthly_fee_cost_3_label,
						agent_revenue_share_percent: config.agent_revenue_share_percent,
					};
				});

				setConfiguration({
					name: configs[0].name,
					description: configs[0].description,
					is_active: configs[0].is_active,
					partner_revenue_share_percent:
						configs[0].partner_revenue_share_percent ?? null,
					tiers: tiersMap as any,
				});
			}
		}
	}, [editConfigId, existingConfigResponse]);

	const updateConfiguration = (updates: Partial<RevenueConfiguration>) => {
		setConfiguration((prev) => ({ ...prev, ...updates }));
	};

	const isCurrentStepValid = () => {
		switch (stepKey) {
			case 'basic':
				return configuration.name.trim() !== '';
			case 'partner': {
				const share = configuration.partner_revenue_share_percent;
				return share == null || (share >= 0 && share <= 100);
			}
			case 'rates':
				// Check that all required fields are filled for all tiers
				return Object.values(configuration.tiers).every(
					(tier) =>
						tier.discount_buy_rate >= 0 &&
						tier.authorization_buy_rate >= 0 &&
						tier.agent_revenue_share_percent >= 0 &&
						tier.agent_revenue_share_percent <= 100,
				);
			default:
				return false;
		}
	};

	const handleSave = async () => {
		try {
			// Convert tiers object to array
			const configurationsArray = Object.values(configuration.tiers);

			const request = {
				name: configuration.name,
				description: configuration.description,
				is_active: configuration.is_active,
				...(pickedPartnerId !== undefined && {
					partner_id: pickedPartnerId || null,
				}),
				partner_revenue_share_percent:
					configuration.partner_revenue_share_percent ?? null,
				configurations: configurationsArray,
			};

			let result;
			if (editConfigId) {
				// Update existing configuration
				result = await updateRevenueConfig({
					id: editConfigId,
					config: request,
				});
			} else {
				// Create new configuration
				result = await createRevenueConfig(request);
			}

			if ('data' in result && result.data?.success) {
				showToast(
					'Success',
					'Revenue configuration saved successfully!',
					'success',
				);
				onComplete(configuration.name);
			} else if ('error' in result) {
				const errorMessage =
					(result.error as any)?.data?.message ||
					'Failed to save revenue configuration';
				showToast('Error', errorMessage, 'error');
			} else {
				showToast('Error', 'Failed to save revenue configuration', 'error');
			}
		} catch (error) {
			console.error('Error saving revenue configuration:', error);
			showToast('Error', 'Failed to save revenue configuration', 'error');
		}
	};

	const handleNext = () => {
		if (currentStep < STEPS.length && isCurrentStepValid()) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const renderNavigationButtons = () => {
		const isFirstStep = currentStep === 1;
		const isLastStep = currentStep === STEPS.length;
		const isStepValid = isCurrentStepValid();
		const isNextDisabled = !editConfigId && !isStepValid;

		return (
			<div className={v9.wizardFoot}>
				<button
					onClick={isFirstStep ? onClose : handlePrevious}
					className={`${v9.btnNeutral} ${v9.btnWide}`}
				>
					{isFirstStep ? 'Cancel' : 'Previous'}
				</button>

				<span className="text-gray-500">
					Step {currentStep} of {STEPS.length}
				</span>

				<button
					onClick={isLastStep ? handleSave : handleNext}
					disabled={isCreating || isUpdating || isNextDisabled}
					className={`${v9.btnPrimary} ${v9.btnWide}`}
				>
					{!isLastStep
						? 'Next'
						: isCreating || isUpdating
							? 'Saving...'
							: editConfigId
								? 'Save'
								: 'Create'}
				</button>
			</div>
		);
	};

	const renderCurrentStep = () => {
		switch (stepKey) {
			case 'basic':
				return (
					<BasicInfoStep
						configuration={configuration as any}
						onUpdate={updateConfiguration as any}
						onNext={handleNext}
						onCancel={onClose}
						hideNavigation={isFullPage}
						flagChecked={configuration.is_active}
						onFlagChange={(checked) =>
							updateConfiguration({ is_active: checked })
						}
						flagLabel="Set as default revenue configuration"
						title="Basic Info"
						subtitle="Name and describe the Schedule A you receive from the processor"
						nameLabel="Configuration name"
						namePlaceholder="Configuration Name"
					/>
				);
			case 'partner':
				return (
					<PartnerCoBrandingStep
						configuration={configuration}
						onUpdate={updateConfiguration}
						partnerId={pickedPartnerId ?? linkedPartnerId}
						onPartnerChange={setPickedPartnerId}
					/>
				);
			case 'rates':
				return (
					<RiskTierStep
						configuration={configuration}
						onUpdate={updateConfiguration}
						hideNavigation={isFullPage}
					/>
				);
			default:
				return null;
		}
	};

	// Show loading state when fetching existing config
	if (editConfigId && isLoadingConfig) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
					<p className="text-gray-600">Loading configuration...</p>
				</div>
			</div>
		);
	}

	// Show error state if config failed to load
	if (editConfigId && !isLoadingConfig && !existingConfigResponse?.data) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<p className="text-red-600 mb-4">Failed to load configuration</p>
					<button
						onClick={onClose}
						className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
					>
						Close
					</button>
				</div>
			</div>
		);
	}

	const containerClasses = isFullPage
		? 'min-h-[calc(100vh-110px)] bg-gray-50'
		: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

	const contentClasses = isFullPage
		? 'w-full max-w-[1440px] mx-auto px-8'
		: 'bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6';

	return (
		<div className={containerClasses}>
			<div className={contentClasses}>
				<div className="flex items-center justify-between py-6">
					<div className="flex items-center gap-4">
						{isFullPage && onBack && (
							<button onClick={onBack} className={v9.btnNeutral}>
								<ArrowLeft className={v9.icon} />
								Back
							</button>
						)}
						<div>
							<h2 className="text-2xl font-semibold text-gray-900">
								{editConfigId
									? `Edit · ${configuration.name}`
									: 'Create Revenue Configuration'}
							</h2>
							<p className="text-base text-gray-500">Schedule A buy rates</p>
						</div>
					</div>
					{!isFullPage && (
						<button
							onClick={onClose}
							className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
						>
							<X className="w-6 h-6 text-gray-600" />
						</button>
					)}
				</div>

				<div className="mx-auto mb-8 flex max-w-[720px] flex-wrap gap-2">
					{STEPS.map((step, index) => {
						const stepNumber = index + 1;
						const state =
							stepNumber < currentStep
								? 'done'
								: stepNumber === currentStep
									? 'current'
									: 'todo';
						return (
							<button
								key={step.key}
								type="button"
								onClick={() =>
									(editConfigId || stepNumber <= currentStep) &&
									setCurrentStep(stepNumber)
								}
								className={wizardStepClass(state)}
							>
								<i className={wizardStepNumClass(state)}>
									{state === 'done' ? (
										<Check className="h-3.5 w-3.5" />
									) : (
										stepNumber
									)}
								</i>
								{step.name}
							</button>
						);
					})}
				</div>

				<div className={v9.wizardBody}>{renderCurrentStep()}</div>

				{isFullPage && renderNavigationButtons()}
			</div>
		</div>
	);
};

export default RevenueConfigWizard;
